import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  Truck, ArrowRight, CheckCircle2, Percent, CalendarClock, Receipt, ShieldCheck, AlertTriangle,
  Calculator, Lock, Sparkles,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { applyPageSeo } from "@/lib/seo";
import { useToolGate } from "@/hooks/useToolGate";
import { AnonSignupLock, CommunityProNudge } from "@/components/ToolGate";

const PAGE_URL = "https://routehaulpathways.com/owner-operator-taxes";
const OG_IMAGE = "https://static.prod-images.emergentagent.com/jobs/ad103537-b988-4363-adb5-ef152c7e8f33/images/4e6fa4782fcd2175314c8a6eccc8709b056e9a3949ecbc04cd85994b5c92feb0.jpeg";
const TITLE = "Owner-Operator Taxes: Self-Employment Tax, Quarterly Estimates & Deductions (2026) | RouteHaul";
const DESCRIPTION =
  "Owner-operator trucking taxes explained for 2026: the 15.3% self-employment tax, quarterly estimated payments, the $80/day per diem, and the deductions (fuel, insurance, depreciation, Section 179) that lower your bill.";

const FREE_LIMIT = 2;
const STORE_KEY = "rh_tax_free_uses_v1";
const SE_RATE = 0.153;
const SE_BASE = 0.9235;

const money = (n) => `$${Math.round(isFinite(n) ? n : 0).toLocaleString()}`;
const num = (v) => parseFloat(v) || 0;

const BASICS = [
  { icon: Percent, title: "Self-employment tax: 15.3%", text: "As an owner-operator you pay both halves of Social Security and Medicare — 12.4% + 2.9% = 15.3% — on your net self-employment income (calculated on about 92.35% of net profit). This is on top of income tax, and it's the piece new operators most often forget to set aside for." },
  { icon: CalendarClock, title: "Quarterly estimated payments", text: "There's no employer withholding your taxes, so the IRS expects you to pay as you go. If you'll owe $1,000+ for the year, you make estimated payments roughly every quarter (mid-April, June, September, and January). Miss them and you can face underpayment penalties." },
  { icon: Receipt, title: "The per diem & key deductions", text: "The 2026 trucker per diem is about $80/day for days away from home. Other big deductions: fuel, insurance, truck payment interest & depreciation, maintenance, tolls, permits, ELD/subscriptions, and factoring fees — all lower your taxable income." },
];

const DEDUCTIONS = [
  "Fuel and diesel exhaust fluid (DEF)",
  "Commercial insurance premiums",
  "Truck loan interest & depreciation (or lease payments)",
  "Per diem for days away from home (~$80/day, 2026)",
  "Maintenance, tires and repairs",
  "Tolls, scales, parking and permits",
  "ELD, load boards, phone & software subscriptions",
  "Factoring fees and bank charges",
  "Section 179 & bonus depreciation (40% for 2026) on equipment",
];

const FAQS = [
  { q: "How much should an owner-operator set aside for taxes?", a: "A common rule of thumb is to set aside 25–30% of your net profit for federal taxes, since you owe 15.3% self-employment tax plus income tax. State income tax (where applicable) is on top. The exact number depends on your income, filing status and deductions — use the estimator on this page for a personalized figure and confirm with an accountant." },
  { q: "What is self-employment tax for truckers?", a: "Self-employment tax is the 15.3% you pay to cover both the employee and employer share of Social Security (12.4%) and Medicare (2.9%). It's calculated on about 92.35% of your net self-employment income, and you can deduct half of it when figuring your income tax." },
  { q: "Do owner-operators have to make quarterly tax payments?", a: "Usually yes. Because no employer withholds taxes for you, the IRS expects estimated payments about four times a year if you'll owe $1,000 or more. The 2026 due dates fall around April 15, June 15, September 15, and January 15 of the following year." },
  { q: "How much is the trucker per diem for 2026?", a: "The per diem for meals and incidental expenses is about $80 per full day away from home in 2026. It's one of the most valuable deductions for over-the-road drivers because it doesn't require saving every meal receipt — just documenting your days on the road." },
  { q: "What can owner-operators write off on taxes?", a: "Ordinary and necessary business costs: fuel, insurance, truck depreciation or lease payments, maintenance and tires, tolls and permits, per diem, ELD and load-board subscriptions, factoring fees, and equipment purchases (often via Section 179 or bonus depreciation). Keep clean records and separate business banking to support every deduction." },
  { q: "What is Section 179 and bonus depreciation?", a: "Both let you deduct the cost of qualifying equipment — like a truck or trailer — faster than normal depreciation. Section 179 lets you expense much of it in year one, and 2026 bonus depreciation allows an additional 40% on eligible property. Used well, they can significantly cut your first-year taxable income — confirm the specifics with your accountant." },
];

const Field = ({ id, label, value, onChange, prefix, suffix }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id} className="text-sm text-slate-700">{label}</Label>
    <div className="relative">
      {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{prefix}</span>}
      <Input id={id} data-testid={`tax-${id}`} type="number" min="0" value={value} onChange={(e) => onChange(e.target.value)} className={`h-11 font-mono tabular ${prefix ? "pl-7" : ""} ${suffix ? "pr-12" : ""}`} />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">{suffix}</span>}
    </div>
  </div>
);

const RowLine = ({ label, value, highlight, muted, testid }) => (
  <div className="flex items-center justify-between">
    <span className={`text-sm ${muted ? "text-slate-400" : "text-slate-300"}`}>{label}</span>
    <span data-testid={testid} className={`font-mono tabular font-bold ${highlight ? "text-brand text-lg" : muted ? "text-slate-300" : "text-white"}`}>{value}</span>
  </div>
);

export default function OwnerOperatorTaxes() {
  const { user, isPro, isCommunity, isAnon, locked, anonFreeLeft, registerUse, ready } = useToolGate(STORE_KEY);

  const [results, setResults] = useState(null);
  const [f, setF] = useState({ netProfit: 80000, incomeTaxRate: 12 });
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => applyPageSeo({
    title: TITLE,
    description: DESCRIPTION,
    canonical: PAGE_URL,
    image: OG_IMAGE,
    imageAlt: "Owner-operator taxes explained — RouteHaul Logistics Pathways",
    seoKey: "taxes",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Article",
          headline: "Owner-Operator Taxes: Self-Employment Tax, Quarterly Estimates & Deductions (2026)",
          description: DESCRIPTION,
          about: "Owner-operator trucking taxes",
          author: { "@type": "Organization", name: "RouteHaul Logistics Pathways" },
          publisher: { "@type": "Organization", name: "RouteHaul Logistics Pathways" },
          mainEntityOfPage: PAGE_URL,
          image: OG_IMAGE,
        },
        {
          "@type": "FAQPage",
          mainEntity: FAQS.map((x) => ({ "@type": "Question", name: x.q, acceptedAnswer: { "@type": "Answer", text: x.a } })),
        },
      ],
    },
  }), []);

  const compute = () => {
    const net = num(f.netProfit);
    const seTaxable = net * SE_BASE;
    const seTax = seTaxable * SE_RATE;
    const halfSE = seTax / 2;
    const incomeBase = Math.max(0, net - halfSE);
    const incomeTax = incomeBase * (num(f.incomeTaxRate) / 100);
    const totalTax = seTax + incomeTax;
    const quarterly = totalTax / 4;
    const setAsidePct = net > 0 ? (totalTax / net) * 100 : 0;
    return { net, seTax, incomeTax, totalTax, quarterly, setAsidePct };
  };

  const handleCalculate = () => {
    if (locked) return;
    setResults(compute());
    registerUse();
  };

  return (
    <div className="min-h-screen bg-white" data-testid="taxes-page">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/" data-testid="taxes-brand" className="flex items-center gap-2 font-heading font-black text-lg tracking-tight text-slate-900">
            <Truck className="w-6 h-6 text-brand" strokeWidth={2.5} />
            <span>RouteHaul<span className="hidden sm:inline text-slate-400 font-bold"> Logistics Pathways</span></span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            {user ? (
              <Link to="/" data-testid="taxes-dashboard"><Button className="h-10 px-4 sm:px-5 rounded-full bg-brand hover:bg-brand-hover text-white font-bold text-xs sm:text-sm tracking-wide uppercase">Go to dashboard</Button></Link>
            ) : (
              <>
                <Link to="/login" data-testid="taxes-login"><Button variant="ghost" className="h-10 px-4 rounded-full text-slate-700 font-bold text-xs sm:text-sm tracking-wide uppercase hover:bg-slate-100">Log in</Button></Link>
                <Link to="/register" data-testid="taxes-header-signup"><Button className="h-10 px-4 sm:px-5 rounded-full bg-brand hover:bg-brand-hover text-white font-bold text-xs sm:text-sm tracking-wide uppercase">Get started</Button></Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6 py-16 sm:py-20">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-brand mb-5">
              <span className="w-8 h-px bg-brand" /> Owner-Operator Taxes
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black text-slate-900 leading-[1.02]">
              Owner-operator taxes, explained
            </h1>
            <p className="mt-5 text-base sm:text-lg leading-relaxed text-slate-700">
              Nobody withholds taxes for you anymore. Here's the plain-English 2026 breakdown of the
              <strong> 15.3% self-employment tax</strong>, quarterly estimated payments, the per diem, and the
              deductions that cut your bill — plus a tool to estimate what to set aside.
            </p>
          </motion.div>

          <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6" data-testid="taxes-quick-answer">
            <div className="flex items-center gap-2 text-sm font-bold text-slate-900 mb-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Quick answer
            </div>
            <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
              Most owner-operators should set aside roughly <strong>25–30% of net profit</strong> for federal
              taxes: a <strong>15.3% self-employment tax</strong> plus income tax. You pay it in
              <strong> quarterly estimated payments</strong> (about April, June, September and January), and you
              lower it with deductions like fuel, insurance, depreciation and the <strong>~$80/day per diem</strong>.
              State income tax may apply on top.
            </p>
          </div>

          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <a href="#tax-estimator" data-testid="taxes-hero-cta">
              <Button className="h-13 px-7 py-3 rounded-full bg-brand hover:bg-brand-hover text-white font-bold group w-full sm:w-auto">
                Estimate what to set aside
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Basics */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter font-black mb-10">The three things every owner-operator owes on</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {BASICS.map((b, i) => (
              <div key={b.title} data-testid={`taxes-basic-${i}`} className="rounded-2xl border border-white/10 bg-white/5 p-7">
                <div className="w-12 h-12 rounded-xl bg-brand/20 flex items-center justify-center mb-5"><b.icon className="w-6 h-6 text-brand" strokeWidth={2.2} /></div>
                <h3 className="font-heading text-lg font-bold mb-2">{b.title}</h3>
                <p className="text-sm text-slate-300 leading-relaxed">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deductions */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter font-black text-slate-900 mb-3">Deductions that lower your bill</h2>
        <p className="text-slate-600 mb-8 max-w-2xl">Every legitimate business expense reduces the income you're taxed on. The big ones for owner-operators:</p>
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
          {DEDUCTIONS.map((d, i) => (
            <div key={i} data-testid={`taxes-deduction-${i}`} className="flex items-start gap-2.5 text-sm text-slate-700">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /> {d}
            </div>
          ))}
        </div>
      </section>

      {/* Tax estimator tool (Pro-gated after 2 free runs) */}
      <section id="tax-estimator" className="bg-slate-50 border-y border-slate-200 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-brand mb-3 flex items-center gap-2"><Calculator className="w-4 h-4" /> Free tool</div>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter font-black text-slate-900">Quarterly Tax Estimator</h2>
          <p className="mt-3 text-slate-600 max-w-2xl leading-relaxed">Enter your expected net profit to estimate your self-employment + income tax and how much to set aside each quarter.</p>

          <div className="mt-8 grid lg:grid-cols-5 gap-8 items-start">
            {/* Inputs */}
            <div className="lg:col-span-3 space-y-6">
              <div className="border border-slate-200 rounded-md p-6 bg-white">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field id="netProfit" label="Expected annual net profit" value={f.netProfit} onChange={set("netProfit")} prefix="$" />
                  <Field id="incomeTaxRate" label="Estimated income-tax rate" value={f.incomeTaxRate} onChange={set("incomeTaxRate")} suffix="%" />
                </div>
                <p className="mt-3 text-xs text-slate-500 leading-relaxed">
                  Net profit = revenue minus business expenses. Your income-tax rate depends on filing status,
                  deductions and other income — 10–22% is a common range. This is a planning estimate, not a filing.
                </p>
              </div>

              {!locked ? (
                <div>
                  <Button onClick={handleCalculate} data-testid="tax-calculate-btn" className="h-14 px-8 rounded-full bg-brand hover:bg-brand-hover text-white text-base font-bold w-full sm:w-auto group">
                    <Calculator className="w-5 h-5 mr-2" /> Estimate my taxes
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                  {ready && isAnon && anonFreeLeft > 0 && (
                    <p className="mt-3 text-sm text-slate-500" data-testid="tax-free-left">
                      1 free calculation — create a free account for unlimited use
                    </p>
                  )}
                  {isCommunity && results && (
                    <div className="mt-6"><CommunityProNudge testid="tax-pro-nudge" perk="save and track your tax set-aside" /></div>
                  )}
                </div>
              ) : (
                <AnonSignupLock testid="tax-anon-lock" perks={[
                  "Unlimited quarterly tax estimates",
                  "The full 12-Step Business Foundation Guide",
                  "Cost-per-mile & startup budget tools",
                ]} />
              )}
            </div>

            {/* Results */}
            <div className="lg:col-span-2 lg:sticky lg:top-24">
              {!results ? (
                <div className="border border-dashed border-slate-300 rounded-md p-10 text-center bg-white" data-testid="tax-empty">
                  <Calculator className="w-8 h-8 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm">Enter your numbers and hit <span className="font-bold text-slate-700">Estimate</span> to see your quarterly set-aside.</p>
                </div>
              ) : (
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="bg-slate-900 text-white rounded-md p-8">
                  <div className="text-xs tracking-[0.2em] uppercase font-bold text-slate-400 mb-2">Set aside each quarter</div>
                  <div className="font-mono font-bold tabular text-brand leading-none" data-testid="tax-quarterly" style={{ fontSize: "clamp(3rem, 7vw, 4.5rem)" }}>{money(results.quarterly)}</div>
                  <div className="text-slate-500 text-sm mt-1">≈ {Math.round(results.setAsidePct)}% of your net profit</div>
                  <div className="mt-8 space-y-3">
                    <RowLine label="Self-employment tax (15.3%)" value={money(results.seTax)} testid="tax-se" />
                    <RowLine label="Estimated income tax" value={money(results.incomeTax)} testid="tax-income" />
                    <div className="h-px bg-white/10 my-2" />
                    <RowLine label="Total annual tax" value={money(results.totalTax)} highlight testid="tax-total" />
                  </div>
                  <div className="mt-6 flex items-start gap-2 text-xs text-slate-400 leading-relaxed">
                    <CalendarClock className="w-4 h-4 text-brand shrink-0 mt-0.5" />
                    Pay it in 4 estimated installments (~Apr, Jun, Sep, Jan). Setting aside a fixed % of every settlement is the easiest way to never come up short.
                  </div>
                </motion.div>
              )}
              <p className="text-xs text-slate-400 mt-4 text-center">Estimate only — not tax advice. Confirm with a CPA.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Common mistake */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <div className="rounded-2xl border-2 border-amber-200 bg-amber-50 p-7">
          <div className="flex items-center gap-2 mb-4"><AlertTriangle className="w-5 h-5 text-amber-600" /><h2 className="font-heading text-xl font-black text-slate-900">The tax mistake that wrecks new owner-operators</h2></div>
          <p className="text-sm text-slate-700 leading-relaxed">
            Spending the whole settlement. Because no one withholds taxes for you, it's easy to treat gross
            revenue as income — then get blindsided by a five-figure tax bill and penalties for skipping
            quarterly payments. Set aside 25–30% of net into a separate account from your very first load and
            the bill never surprises you.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand text-white">
        <div className="max-w-3xl mx-auto px-6 py-16 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter font-black leading-tight">Run the business side like a pro</h2>
          <p className="mt-4 text-orange-50 text-lg">Create a free account for the 12-Step Business Foundation Guide — including how to set up your books, taxes and cash flow from day one.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register" data-testid="taxes-final-cta">
              <Button className="h-14 px-8 rounded-full bg-white text-brand hover:bg-orange-50 text-base font-bold group w-full sm:w-auto">
                Start the free guide
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/how-much-do-owner-operators-make" data-testid="taxes-salary-cta">
              <Button variant="outline" className="h-14 px-8 rounded-full border-white/60 bg-transparent text-white hover:bg-white/10 text-base font-bold w-full sm:w-auto">
                How much do owner-operators make?
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-slate-50 border-t border-slate-200">
        <div className="max-w-3xl mx-auto px-6 py-16">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter font-black text-slate-900 text-center mb-10">Frequently asked questions</h2>
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map((x, i) => (
              <AccordionItem key={i} value={`txfaq-${i}`} data-testid={`taxes-faq-${i}`} className="border-slate-200">
                <AccordionTrigger className="text-left font-heading font-bold text-slate-900 hover:no-underline">{x.q}</AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed">{x.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <p className="mt-8 inline-flex items-center gap-2 text-sm text-slate-500">
            <ShieldCheck className="w-4 h-4" /> Educational mentorship — not tax, legal, or financial advice. Confirm your situation with a licensed CPA or the IRS.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-8 text-sm text-slate-400 flex items-center justify-between flex-wrap gap-2">
          <span>© {new Date().getFullYear()} RouteHaul Logistics Pathways</span>
          <div className="flex items-center gap-4">
            <Link to="/" className="hover:text-slate-900">Home</Link>
            <Link to="/how-much-does-it-cost-to-start-a-trucking-company" className="hover:text-slate-900">Startup Costs</Link>
            <Link to="/how-much-do-owner-operators-make" className="hover:text-slate-900">Owner-Operator Pay</Link>
            <Link to="/ifta-fuel-tax-calculator" className="hover:text-slate-900">IFTA Calculator</Link>
            <Link to="/owner-operator-faq" className="hover:text-slate-900">FAQ</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
